<script setup lang="ts">
// NPM
import { ref } from 'vue';
import { useMq } from 'vue3-mq';

// Components
import {
  BaseCard,
  BaseEmptyState,
  Modal,
  BaseButton,
  BaseBadge,
} from '@magma-app/magma-lapilli';

// API
import { useEvents } from '@/api/events/use-events';
import { useActionDeepLink } from '@/composables/useActionDeepLink';

// Plugins
const mq = useMq();

const { data: events, isLoading } = useEvents();

const modal = ref<number | null>(null);

// Opens one action's modal directly from a ?eventId= deep link.
useActionDeepLink({
  type: 'event',
  bucket: 'rejected',
  onOpen: (_item, id) => {
    modal.value = id;
  },
});

const getEventImgUrl = (event: { pictureUrl?: string | null }) =>
  event.pictureUrl
    ? event.pictureUrl
    : mq.lgPlus
      ? '/img/event_lg.png'
      : '/img/event.png';
</script>

<template>
  <div
    v-if="!isLoading"
    class="block h-full flex-1 px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap': events?.rejected.length
    }"
  >
    <template v-if="events?.rejected.length">
      <Modal
        v-for="event in events?.rejected"
        :key="event.userEventId"
        :open="modal === event.eventId"
        content-class="max-w-[90dvw] sm:max-w-[480px]"
        @open-focus="(e) => e.preventDefault()"
        @open-change="
          (open) => {
            modal = event.eventId;

            if (!open) {
              modal = null;
            }
          }
        "
      >
        <template #trigger>
          <BaseCard
            aria-hidden="true"
            class="mb-2 lg:mb-6 lg:mr-6"
            :title="event.name"
            :img-url="getEventImgUrl(event)"
            :points="event.points ?? undefined"
            :badge-status="{ points: event.points ?? 0, type: 'event' }"
            :display-points="!event.pictureUrl"
            :date="event.date"
          />
        </template>
        <template #content>
          <div>
            <img
              :src="getEventImgUrl(event)"
              :alt="event.name"
              class="h-[200px] w-full rounded-[5px] object-cover mb-5"
            />
            <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
              {{ event.name }}
            </h3>
            <BaseBadge
              class="mt-2"
              size="md"
              :text="event.points ?? 0"
              icon-type="icon"
              icon-name="star"
            />
            <div
              class="rich-text-content text-mgm-txt-sm text-foreground-default my-5 max-h-[40dvh] lg:max-h-auto overflow-y-auto"
              v-html="event.description"
            ></div>
            <BaseButton
              size="lg"
              disabled
              class="!bg-background-danger-default w-full !text-foreground-danger-default border-none"
              >{{ $t('events.rejected.cta') }}</BaseButton
            >
          </div>
        </template>
      </Modal>
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('eventsView.empty.all')"
      type="empty-list"
    />
  </div>
</template>
